import { useCallback } from 'react';

import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useUnit } from 'effector-react';

import { customerSubscriptionJoinCodeQueryOptions } from '@m/hooks/data/customer/subscriptions';
import { membershipStore } from 'apps/customer/state/membership';

/**
 * Asks the backend whether the signed-in user may see the invited individual subscription.
 * `plan-for-user` matches the plan's allow-list against the user's saved phone number and email,
 * and answers with a null `subscriptionPlan` when a hidden plan isn't theirs.
 *
 * Resolves `false` whenever the plan can't be offered — not on the allow-list, the plan doesn't
 * exist (the backend errors), or the request failed — so the user is blocked rather than reaching
 * the review step with nothing to buy.
 */
export function useIndividualSubscriptionEligibility(): () => Promise<boolean> {
  const queryClient = useQueryClient();
  const { individualSubscriptionInvitationId } = useUnit(membershipStore);

  return useCallback(async () => {
    if (!individualSubscriptionInvitationId) return false;
    try {
      // staleTime 0: the user's email may have just changed, so never answer from cache.
      const data = await queryClient.query({
        ...customerSubscriptionJoinCodeQueryOptions(individualSubscriptionInvitationId),
        staleTime: 0,
      });
      return !!data?.subscriptionPlan;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }, [individualSubscriptionInvitationId, queryClient]);
}
